// Monthly Profit & Loss — one calculation, shown on Finance › P&L and exported
// from Reports.
//
// Revenue is claim_lines: accrual by service date, cash by paid date. Since
// 2026-09-25 that also covers claims filed outside CAERA (source = 'manual').
// DME is excluded — dme_orders has no billed-amount column yet. Payroll cost
// is every payroll run whose week_start falls in the month (a run is not split
// across a month boundary), draft or finalized, so the report reflects what is
// in the system.
//
// Overhead comes from QuickBooks for any month the books have been synced
// (accounts treated as `overhead`, lib/quickbooks.js), and from the Overhead
// tab otherwise. Never both for one month: the books already hold whatever
// someone would type in, and adding the two would count it twice.
import { supabase } from './supabase';
import { summariseBooks } from './quickbooks';
import {
  format, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters,
  startOfYear, endOfYear, subYears, eachMonthOfInterval,
} from 'date-fns';

export const PL_PRESETS = [
  { key: 'this_month',   label: 'This Month',     range: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
  { key: 'last_month',   label: 'Last Month',     range: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] },
  { key: 'this_quarter', label: 'This Quarter',   range: () => [startOfQuarter(new Date()), endOfQuarter(new Date())] },
  { key: 'last_quarter', label: 'Last Quarter',   range: () => [startOfQuarter(subQuarters(new Date(), 1)), endOfQuarter(subQuarters(new Date(), 1))] },
  { key: 'this_year',    label: 'This Year',      range: () => [startOfYear(new Date()), endOfYear(new Date())] },
  { key: 'last_year',    label: 'Last Year',      range: () => [startOfYear(subYears(new Date(), 1)), endOfYear(subYears(new Date(), 1))] },
  { key: 'last_6',       label: 'Last 6 Months',  range: () => [startOfMonth(subMonths(new Date(), 5)), endOfMonth(new Date())] },
  { key: 'last_12',      label: 'Last 12 Months', range: () => [startOfMonth(subMonths(new Date(), 11)), endOfMonth(new Date())] },
  { key: 'custom',       label: 'Custom Range',   range: null },
];

export const fmtMoney = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The P&L for every calendar month touching [startDate, endDate].
 * Returns { months: [{ key, label, revAccrual, revCash, payroll, overhead,
 * netAccrual, netCash }], totals, rangeLabel }.
 */
export const fetchMonthlyPL = async (startDate, endDate) => {
  const rangeStart = format(startOfMonth(startDate), 'yyyy-MM-dd');
  const rangeEnd   = format(endOfMonth(endDate),   'yyyy-MM-dd');

  const [{ data: lines }, { data: payrollRuns }, { data: overhead }, { data: qboRows }, { data: qboAccounts }] = await Promise.all([
    supabase.from('claim_lines').select('service_date, paid_date, accrued_amount, paid_amount')
      .or(`and(service_date.gte.${rangeStart},service_date.lte.${rangeEnd}),and(paid_date.gte.${rangeStart},paid_date.lte.${rangeEnd})`),
    supabase.from('payroll_runs').select('week_start, total_pay').gte('week_start', rangeStart).lte('week_start', rangeEnd),
    supabase.from('overhead_expenses').select('month, amount').gte('month', rangeStart).lte('month', rangeEnd),
    // Readable by finance managers only; anyone else gets no rows and the
    // Overhead tab figures, which is what they saw before.
    supabase.from('qbo_pl_monthly').select('month, account_id, amount').gte('month', rangeStart).lte('month', rangeEnd),
    supabase.from('qbo_accounts').select('account_id, section, treatment'),
  ]);
  const books = summariseBooks(qboRows, qboAccounts);

  const months = eachMonthOfInterval({ start: startOfMonth(startDate), end: startOfMonth(endDate) })
    .map(d => ({ key: format(d, 'yyyy-MM'), label: format(d, 'MMMM yyyy'), revAccrual: 0, revCash: 0, payroll: 0, overhead: 0, manualOverhead: 0, overheadSource: 'manual' }));
  const byKey = Object.fromEntries(months.map(m => [m.key, m]));
  const monthKey = (dateStr) => dateStr ? dateStr.slice(0, 7) : null;

  for (const l of lines || []) {
    const svc = byKey[monthKey(l.service_date)];
    if (svc) svc.revAccrual += Number(l.accrued_amount) || 0;
    const paid = byKey[monthKey(l.paid_date)];
    if (paid) paid.revCash += Number(l.paid_amount) || 0;
  }
  for (const r of payrollRuns || []) {
    const m = byKey[monthKey(r.week_start)];
    if (m) m.payroll += Number(r.total_pay) || 0;
  }
  for (const o of overhead || []) {
    const m = byKey[monthKey(o.month)];
    if (m) m.manualOverhead += Number(o.amount) || 0;
  }
  for (const m of months) {
    if (books[m.key]) { m.overhead = books[m.key].overhead; m.overheadSource = 'quickbooks'; }
    else m.overhead = m.manualOverhead;
    m.netAccrual = m.revAccrual - m.payroll - m.overhead;
    m.netCash    = m.revCash    - m.payroll - m.overhead;
  }
  const totals = months.reduce((t, m) => ({
    revAccrual: t.revAccrual + m.revAccrual, revCash: t.revCash + m.revCash,
    payroll: t.payroll + m.payroll, overhead: t.overhead + m.overhead,
  }), { revAccrual: 0, revCash: 0, payroll: 0, overhead: 0 });
  totals.netAccrual = totals.revAccrual - totals.payroll - totals.overhead;
  totals.netCash    = totals.revCash    - totals.payroll - totals.overhead;

  const rangeLabel = months.length === 1 ? months[0].label : `${months[0].label} to ${months[months.length - 1].label}`;
  return { months, totals, rangeLabel };
};

export const PL_HEADERS = ['Month', 'Revenue (Accrual)', 'Revenue (Cash)', 'Payroll Cost', 'Overhead Cost', 'Net Income (Accrual)', 'Net Income (Cash)'];

/** The P&L as report rows, for CSV and PDF. */
export const plReportRows = ({ months, totals }) => {
  const rows = months.map(m => [
    m.label, fmtMoney(m.revAccrual), fmtMoney(m.revCash), fmtMoney(m.payroll), fmtMoney(m.overhead),
    fmtMoney(m.netAccrual), fmtMoney(m.netCash),
  ]);
  if (months.length > 1) {
    rows.push([
      `Total (${months.length} months)`, fmtMoney(totals.revAccrual), fmtMoney(totals.revCash), fmtMoney(totals.payroll),
      fmtMoney(totals.overhead), fmtMoney(totals.netAccrual), fmtMoney(totals.netCash),
    ]);
  }
  return rows;
};
